import { DatePicker } from "@/common/components/DatePicker";
import { ApiError } from "@/common/lib/api";
import { validateCedulaCR } from "@/common/lib/validateCedulaCR";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AmountInput } from "@/features/cost-allocations/components/AmountInput";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useRegisterTermination } from "../api/animalsApi";
import type { AnimalDetail } from "../types";

type Reason = "died" | "culled" | "sold";

const REASON_LABEL: Record<Reason, string> = {
  died: "Murió",
  culled: "Descarte",
  sold: "Venta",
};

type Props = {
  accountId: string;
  animal: AnimalDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function RegisterTerminationDialog({
  accountId,
  animal,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const mutation = useRegisterTermination(accountId, animal.id);

  // B77 (decisión 2026-06-12): un animal descartado (culled_for_sale) solo
  // admite venta o muerte como evento final — el re-descarte da 409 en el
  // backend, así que ni se ofrece.
  const isCulledForSale = animal.lifecycleState === "culled_for_sale";

  const [reason, setReason] = useState<Reason | "">("");
  const [occurredAt, setOccurredAt] = useState<Date | undefined>(undefined);
  const [cause, setCause] = useState("");
  const [culledReason, setCulledReason] = useState("");
  const [price, setPrice] = useState(""); // canonical decimal string
  const [buyer, setBuyer] = useState("");
  // B67 (facturas-v1 §3.2): datos de la factura emitida — visibles solo
  // cuando el tipo es venta. Default sale_condition: contado (spec).
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [saleCondition, setSaleCondition] = useState<"contado" | "credito">("contado");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setOccurredAt(new Date());
    setCause("");
    setCulledReason("");
    setPrice("");
    setBuyer("");
    setInvoiceNumber("");
    setBuyerId("");
    setSaleCondition("contado");
    setError(null);
  }, [open]);

  const birthDate = new Date(animal.birthDate);
  const today = new Date();
  const pending = mutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Guard sincrónico contra double-submit. `mutation.isPending` se setea
    // ANTES de que React re-renderize, así que un segundo submit dentro del
    // mismo frame (Enter spam, doble click rápido) ve el flag ya activo y
    // sale temprano. La defensa de UI con `disabled` cubre la mayoría de
    // casos pero hay un frame de gap; este guard lo cierra.
    if (mutation.isPending) return;
    setError(null);

    if (!reason) {
      setError("Selecciona la razón de la terminación.");
      return;
    }
    if (!occurredAt) {
      setError("Selecciona la fecha de la terminación.");
      return;
    }
    if (occurredAt > new Date()) {
      setError("La fecha no puede ser futura.");
      return;
    }
    if (occurredAt < birthDate) {
      setError("La fecha no puede ser anterior al nacimiento del animal.");
      return;
    }
    // AI-D26 P2: espejo del guard FINAL_EVENT_BEFORE_CULL del backend — la
    // venta/muerte de un descartado no puede fecharse antes del descarte
    // (timeline contradictoria; el asiento saldría de 1.4.1 antes de que el
    // valor llegara ahí). Comparación por día calendario, mismo día permitido.
    if (isCulledForSale && animal.retiredAt !== null) {
      const occurredDay = new Date(occurredAt);
      occurredDay.setHours(12, 0, 0, 0);
      if (occurredDay.toISOString().slice(0, 10) < animal.retiredAt) {
        setError(
          `La fecha no puede ser anterior al descarte (${animal.retiredAt}). Si la fecha del descarte está mal, corregí ese evento primero.`,
        );
        return;
      }
    }

    if (reason === "died" && cause.trim().length === 0) {
      setError("Indica la causa de la muerte.");
      return;
    }
    if (reason === "sold") {
      if (!price || Number(price) <= 0) {
        setError("Ingresa el monto de la venta.");
        return;
      }
    }

    // Normalizar a mediodía local para evitar rollover UTC: si el user submitea
    // de noche en CR (UTC-6), `occurredAt.toISOString().slice(0,10)` da el día
    // siguiente. El backend trunca occurredAt para guardar `retired_at` como
    // YYYY-MM-DD vía `.toISOString().slice(0,10)` (stateMachine.ts), así que
    // mediodía local nos protege en cualquier TZ.
    const normalized = new Date(occurredAt);
    normalized.setHours(12, 0, 0, 0);
    const occurredAtIso = normalized.toISOString();

    try {
      if (reason === "died") {
        await mutation.mutateAsync({
          reason: "died",
          occurredAt: occurredAtIso,
          cause: cause.trim(),
        });
      } else if (reason === "culled") {
        await mutation.mutateAsync({
          reason: "culled",
          occurredAt: occurredAtIso,
          reasonText: culledReason.trim() || undefined,
        });
      } else {
        await mutation.mutateAsync({
          reason: "sold",
          occurredAt: occurredAtIso,
          price,
          buyer: buyer.trim() || undefined,
          invoiceNumber: invoiceNumber.trim() || undefined,
          buyerId: buyerId.trim() || undefined,
          saleCondition,
        });
      }
      toast.success(`Terminación registrada · ${REASON_LABEL[reason]}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError("Ocurrió un error inesperado.");
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isCulledForSale ? "Registrar venta o muerte" : "Registrar terminación"}
          </DialogTitle>
          <DialogDescription>
            {isCulledForSale
              ? "El animal está descartado, en espera de venta. Registrá acá su venta o, si murió esperando, su muerte. Esto cierra su ciclo definitivamente."
              : "El animal queda dado de baja. Esta acción cierra el ciclo contable y no acepta más eventos operativos."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="termination-reason" className="font-medium text-[13px] text-app-ink-2">
              Razón
            </label>
            <Select value={reason} onValueChange={(v) => setReason(v as Reason)} disabled={pending}>
              <SelectTrigger
                id="termination-reason"
                className="h-10 w-full text-app-ink-1"
                aria-label="Razón"
              >
                <SelectValue placeholder="Selecciona una razón" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="died">Murió</SelectItem>
                {!isCulledForSale && <SelectItem value="culled">Descarte</SelectItem>}
                <SelectItem value="sold">Venta</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="termination-date" className="font-medium text-[13px] text-app-ink-2">
              Fecha
            </label>
            <DatePicker
              id="termination-date"
              aria-label="Fecha de terminación"
              value={occurredAt}
              onChange={setOccurredAt}
              minDate={birthDate}
              maxDate={today}
              disabled={pending}
            />
          </div>

          {reason === "died" && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="termination-cause" className="font-medium text-[13px] text-app-ink-2">
                Causa
              </label>
              <input
                id="termination-cause"
                type="text"
                value={cause}
                onChange={(e) => setCause(e.target.value)}
                maxLength={500}
                disabled={pending}
                placeholder="Ej. Enfermedad respiratoria"
                className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
              />
            </div>
          )}

          {reason === "culled" && (
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="termination-culled-reason"
                className="font-medium text-[13px] text-app-ink-2"
              >
                Motivo <span className="font-normal text-app-ink-3">(opcional)</span>
              </label>
              <input
                id="termination-culled-reason"
                type="text"
                value={culledReason}
                onChange={(e) => setCulledReason(e.target.value)}
                maxLength={500}
                disabled={pending}
                placeholder="Ej. Improductiva, lesión"
                className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
              />
            </div>
          )}

          {reason === "sold" && (
            <>
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="termination-price"
                  className="font-medium text-[13px] text-app-ink-2"
                >
                  Monto de la venta
                </label>
                <AmountInput
                  id="termination-price"
                  value={price}
                  onChange={setPrice}
                  disabled={pending}
                  aria-label="Monto de la venta en colones"
                />
                <span className="text-[12px] text-app-ink-3">Solo CRC en esta versión.</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="termination-buyer"
                  className="font-medium text-[13px] text-app-ink-2"
                >
                  Comprador <span className="font-normal text-app-ink-3">(opcional)</span>
                </label>
                <input
                  id="termination-buyer"
                  type="text"
                  value={buyer}
                  onChange={(e) => setBuyer(e.target.value)}
                  maxLength={200}
                  disabled={pending}
                  placeholder="Nombre del comprador"
                  className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
                />
              </div>

              {/* B67 (facturas-v1 §3.2): datos de la factura emitida */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="termination-invoice-number"
                  className="font-medium text-[13px] text-app-ink-2"
                >
                  # Factura <span className="font-normal text-app-ink-3">(opcional)</span>
                </label>
                <input
                  id="termination-invoice-number"
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  maxLength={50}
                  disabled={pending}
                  placeholder="# de la factura emitida"
                  className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 font-mono text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="termination-buyer-id"
                  className="font-medium text-[13px] text-app-ink-2"
                >
                  Cédula del comprador{" "}
                  <span className="font-normal text-app-ink-3">(opcional)</span>
                </label>
                <input
                  id="termination-buyer-id"
                  type="text"
                  inputMode="numeric"
                  value={buyerId}
                  onChange={(e) => setBuyerId(e.target.value)}
                  maxLength={20}
                  disabled={pending}
                  placeholder="Física (9 dígitos) o jurídica (3-...)"
                  className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 font-mono text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
                />
                {buyerId.trim() !== "" && !validateCedulaCR(buyerId).valid && (
                  <output className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                    No parece una cédula CR válida — verificá. Se puede guardar igual.
                  </output>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="termination-sale-condition"
                  className="font-medium text-[13px] text-app-ink-2"
                >
                  Condición de venta
                </label>
                <Select
                  value={saleCondition}
                  onValueChange={(v) => setSaleCondition(v as "contado" | "credito")}
                  disabled={pending}
                >
                  <SelectTrigger
                    id="termination-sale-condition"
                    className="h-10 w-full text-app-ink-1"
                    aria-label="Condición de venta"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contado">Contado</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {error && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                disabled={pending}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
              >
                Cancelar
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={pending}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
            >
              {pending ? "Registrando…" : "Registrar terminación"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
